import { autoinject, bindable, observable } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { apiCall } from 'app/shared/lib/essette-api';
import { HouseholdService } from 'app/cd/services/household-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { MemberSelect } from 'app/cd/components/member-select';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class EditHouseholdMembers extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected householdId?: string;

    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;
    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;

    @observable
    public selectedMember: CD.Member = {};

    protected householdMembers: CD.Member[] = [];
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $memberSelect!: MemberSelect;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: HouseholdService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.checkPermissions();
    }


    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        this.resultCount = 0;
        if(this.$pager) this.$pager.page = 0;

        // Default Sort
        this.sorting = new Sorting();
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];

        await this.loadHouseholdMembers();
    }

    protected async householdIdChanged(): Promise<void> {
        this.householdMembers = [];
        await this.reload();
    }

    protected async selectedMemberChanged(): Promise<void> {
        if(this.selectedMember?.id) await this.insertHouseholdMember();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'description',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.loadHouseholdMembers();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    protected buildHousehold(): CD.Household {
        const household: CD.Household = {
            id: this.householdId,
            members: this.householdMembers.map(member => {
                // retain existing members by mapping over their IDs
                return { id: member.id } })
        };

        // then add the newly selected member's ID
        household.members!.push({ id: this.selectedMember.id });

        return household;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadHouseholdMembers(): Promise<void> {
        if(!this.householdId) return;

        try {
            this.state.beginLoad();
            const includes: string[] = [
                'primaryPlan.lineOfBusiness',
                'primaryPlan.healthPlan',
                'primaryCarePhysician',
                'membership'
            ];

            const options: Partial<IQueryLoadOptions> = {
                top: this.pageSize,
                sorting: this.sorting,
                page: this.$pager?.page || 0,
                includeInactive: false
            };

            const response = await this.service.loadHouseholdMembers(this.householdId, options, includes);
            this.householdMembers = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async insertHouseholdMember(): Promise<void> {
        if(!this.selectedMember?.id) return;

        let isSuccessful = false;
        try {
            this.state.beginLoad();

            const response = await this.service.saveHousehold(this.buildHousehold());
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.added('cd:household.member.$', isSuccessful);
            if(isSuccessful) {
                if(this.$memberSelect) this.$memberSelect.clear();
                this.selectedMember = {};
                this.reload();
            }
            this.state.endLoad();
        }
    }

    @apiCall
    private async activateMembership(membership: CD.HouseholdMembership): Promise<void> {
        if(!membership.id) return;

        if(membership.isActive) {
            const result = await confirmation.showDeactivate('cd:household.member.$', 1, false);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginLoad();

            const response = await this.service.activateHouseholdMembership(membership.id, !membership.isActive);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:household.member.$', !membership.isActive, isSuccessful);
            if(isSuccessful) this.reload();
            this.state.endLoad();
        }
    }
}
